import React from "react";
import { Link, useLocation } from "react-router-dom";
import "./Footer.scss";
import logoPhotographer from "../../../images/logo-header-photographer.webp";
import InstagramIcon from "../../../images/instagram-icon.svg";
import FacebookIcon from "../../../images/facebook-icon.svg";
import TiktokIcon from "../../../images/tiktok-icon.svg";
import { useAppSelector } from "../../../redux/hooks";

const socialLinks = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/lobachevaphotography/",
    icon: InstagramIcon,
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/Alen4ikLobacheva?mibextid=9R9pXO",
    icon: FacebookIcon,
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@lobachevaphotography/",
    icon: TiktokIcon,
  },
];

const Footer: React.FC = () => {
  const { pathname } = useLocation();
  const { auth } = useAppSelector((state) => state.user);
  const currentYear = new Date().getFullYear();
  const isContactPage = pathname === "/contact/" || pathname === "/contact";
  const isAboutPage = pathname === "/about/" || pathname === "/about";
  const isResourcesPage = pathname === "/resources" || pathname.startsWith("/resources/");
  const isPricingPage = pathname === "/prices" || pathname.startsWith("/prices/");
  const isNotFoundPage = pathname === "/not-found/";
  const showFooterCta = !isContactPage && !isAboutPage && !isResourcesPage && !isPricingPage && !isNotFoundPage;

  return (
    <footer className="footer">
      {showFooterCta && (
        <section className="footer__cta" aria-labelledby="footer-cta-title">
          <div className="footer__ctaCopy">
            <p className="footer__eyebrow">Ready when you are</p>
            <h2 className="footer__ctaTitle" id="footer-cta-title">
              Let&apos;s create something meaningful together.
            </h2>
          </div>
          <Link className="footer__ctaLink" to="/contact/">
            Start a conversation <span aria-hidden="true">→</span>
          </Link>
        </section>
      )}

      <div className="footer__main">
        <div className="footer__grid">
          <section className="footer__brand" aria-label="Alena Lobacheva Photography">
            <img
              className="footer__brandLogo"
              src={logoPhotographer}
              width="283"
              height="80"
              alt="Alena Lobacheva Photography, Charlotte newborn photographer"
            />
            <p className="footer__brandText">
              Newborn, maternity, baby, and family photography in Charlotte, North Carolina and surrounding communities.
            </p>
          </section>

          <nav className="footer__nav" aria-label="Explore">
            <p className="footer__columnTitle">Explore</p>
            <div className="footer__navLinks">
              <Link to="/gallery/newborn/">Gallery</Link>
              <Link to="/prices/newborn/">Investment</Link>
              <Link to="/resources/">Resources</Link>
              <Link to="/about/">About Alena</Link>
            </div>
          </nav>

          <nav className="footer__nav" aria-label="Photography sessions">
            <p className="footer__columnTitle">Sessions</p>
            <div className="footer__navLinks">
              <Link to="/prices/newborn/">Newborn</Link>
              <Link to="/prices/maternity/">Maternity</Link>
              <Link to="/prices/baby/">Baby</Link>
              <Link to="/prices/family/">Family</Link>
            </div>
          </nav>

          <section className="footer__follow" aria-labelledby="footer-follow-title">
            <p className="footer__columnTitle" id="footer-follow-title">
              Follow along
            </p>
            <p className="footer__followText">Recent sessions, helpful inspiration, and a little behind the scenes.</p>
            <a
              className="footer__instagramHandle"
              href="https://www.instagram.com/lobachevaphotography/"
              target="_blank"
              rel="noopener noreferrer"
            >
              @lobachevaphotography
            </a>
            <div className="footer__socialLinks" aria-label="Social media">
              {socialLinks.map(({ name, href, icon }) => (
                <a
                  key={name}
                  className="footer__socialLink"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Alena Lobacheva Photography on ${name}`}
                >
                  <img src={icon} alt={`${name} profile for Alena Lobacheva Photography`} />
                </a>
              ))}
            </div>
          </section>

          <div className="footer__bottom">
            <p className="footer__copyright">
              <span>&copy; 2017–{currentYear}</span>
              <span>Alena Lobacheva Photography.</span>
              <span>
                All rights reserved.{" "}
                <a className="footer__sitemapInline" href="/sitemap.xml">
                  Sitemap
                </a>
              </span>
            </p>

            <div className="footer__utilityLinks">
              <Link to="/blog/">Photography archive</Link>
              {!auth && (
                <Link to="/admin/" rel="nofollow">
                  Admin
                </Link>
              )}
              <a className="footer__sitemapLink" href="/sitemap.xml">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
